// زرّ «📢 إعلان» — مُنشئ الإعلان للمالك/الأدمن، مستقلٌّ عن جرس الإشعارات.
//
// يفتح درجًا حقيقيًا (نفس الدرج المشترك: `fixed` عبر Portal إلى body)، فلا يوسّع الصفحة ولا
// يزيح تخطيطها ولا يُنشئ تمريرًا أفقيًا. والإرسال يمرّ على `/admin/announcements` القائم —
// والخادم يفرض أن الأدمن وحده يرسل، فظهور الزرّ ترتيبٌ للواجهة لا حماية.
import { useEffect, useMemo, useState } from 'react';
import { ApiError, get, post } from '../lib/api.ts';
import { Alert, Drawer, Field, Select, TextArea, TextInput } from '../components/ui.tsx';
import { useSession } from '../auth/session.tsx';
import { DAILY_TIPS, TIP_CATEGORIES, tipIndexForDate, type TipCategory } from './dailyTips.ts';

/** عنوان الإعلان حين يُرسَل من نصيحة — وبه يُعرف في السجلّ أنها أُرسلت قبل ذلك */
const tipTitle = (t: { title: string }) => `💡 ${t.title}`;

/**
 * **مكتبة النصايح اليومية — للمالك وحده** (قرار المالك 04/10/2026: «كل يوم ابعت نصيحه للشباب تنفعهم و خلى الكلام دا عندى انا
 * فقط مش عند محمد ابراهيم… كل يوم اختار حاجه ابعتها للشباب كاعلان»). نصيحة اليوم تُقترح بدورانٍ على التاريخ، ويقلّب غيرها
 * ويرشّح بالباب، و«استخدم دي» تملأ العنوان والنصّ فيعدّل ويرسل. وما أُرسل قبل ذلك يُعلَّم ✓ من سجلّ الإعلانات نفسه (بالعنوان)
 * — فلا تتكرّر نصيحةٌ إلّا بقصده، ومن أي جهاز.
 */
function TipsPanel({ onPick }: { onPick: (title: string, body: string) => void }) {
  const [cat, setCat] = useState<TipCategory | ''>('');
  const [idx, setIdx] = useState(() => tipIndexForDate());
  const [sent, setSent] = useState<Set<string>>(new Set());
  useEffect(() => {
    get<{ title: string }[]>('/admin/announcements?limit=200')
      .then(rows => setSent(new Set(rows.map(r => r.title))))
      .catch(() => { /* السجلّ زينةٌ هنا: بلا شبكة تبقى المكتبة تعمل */ });
  }, []);
  const list = useMemo(() => (cat ? DAILY_TIPS.filter(t => t.cat === cat) : DAILY_TIPS), [cat]);
  const tip = list[idx % list.length];
  const wasSent = (t: { title: string }) => sent.has(tipTitle(t));
  const sentCount = list.filter(wasSent).length;
  const catOf = TIP_CATEGORIES.find(c => c.code === tip.cat)!;
  /** التالية التي لم تُرسَل بعد — وإن أُرسلت كلها فالتالية ببساطة */
  const next = () => {
    for (let k = 1; k <= list.length; k++) {
      const cand = list[(idx + k) % list.length];
      if (!wasSent(cand)) { setIdx(idx + k); return; }
    }
    setIdx(idx + 1);
  };
  return (
    <section className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3" aria-label="نصيحة النهارده">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-amber-900">💡 نصيحة النهارده للشباب <span className="font-normal text-amber-800">(ليك إنت بس)</span></h3>
        <span className="text-xs text-amber-800">اتبعت {sentCount} من {list.length}</span>
      </div>
      <div className="mt-2">
        <Select value={cat} aria-label="باب النصيحة" onChange={e => { setCat(e.target.value as TipCategory | ''); setIdx(tipIndexForDate()); }}
          options={[{ code: '', ar: 'كل الأبواب' }, ...TIP_CATEGORIES.map(c => ({ code: c.code, ar: `${c.icon} ${c.ar}` }))]} />
      </div>
      <article className="mt-2 rounded-lg border border-amber-200 bg-white p-3">
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
          <span>{catOf.icon} {catOf.ar}</span>
          {wasSent(tip) && <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800">✓ اتبعتت قبل كده</span>}
        </div>
        <p className="mt-1 text-sm font-bold text-ink-900">{tip.title}</p>
        <p className="mt-1 text-sm leading-6 text-ink-700">{tip.body}</p>
      </article>
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" className="btn-primary flex-1 px-3 py-1.5 text-sm" onClick={() => onPick(tipTitle(tip), tip.body)}>
          استخدم النصيحة دي
        </button>
        <button type="button" className="btn-ghost px-3 py-1.5 text-sm" onClick={next}>نصيحة تانية</button>
      </div>
      <p className="mt-2 text-xs text-ink-500">تقدر تعدّل في الكلام قبل ما تبعته — وهيتبعت كإعلان عادي زي أي إعلان.</p>
    </section>
  );
}

const SEVERITIES = [
  { code: 'INFO', ar: 'إعلان عادي' },
  { code: 'WARNING', ar: 'تنبيه' },
  { code: 'CRITICAL', ar: 'عاجل' },
];

export function AnnounceButton() {
  const { me } = useSession();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState('INFO');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setBusy(true); setError(null); setDone(null);
    try {
      const r = await post<{ sent: number }>('/admin/announcements',
        { title: title.trim(), body: body.trim(), severity });
      setDone(`أُرسل الإعلان إلى ${r.sent} موظفًا. يظهر لهم نصّه كاملًا فور فتح الشاشة.`);
      setTitle(''); setBody('');
    } catch (e) { setError(e instanceof ApiError ? e.message : 'تعذّر إرسال الإعلان'); }
    finally { setBusy(false); }
  }

  const ready = title.trim().length >= 2 && body.trim().length >= 2;

  return (
    <>
      <button type="button" className="btn-ghost px-3 py-1.5 text-sm" onClick={() => setOpen(true)}
        aria-haspopup="dialog" aria-label="إرسال إعلان للموظفين">
        <span aria-hidden="true">📢</span> إعلان
      </button>

      {open && (
        <Drawer title="📢 إرسال إعلان" side="right" tone="brand" onClose={() => setOpen(false)}>
          <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-card">
            <p className="text-sm text-ink-700">
              المرسِل: <strong>{me?.fullNameAr ?? ''}</strong>{me?.isOwner ? ' (المالك)' : ''}
            </p>
            <p className="mt-1 text-xs text-ink-500">
              يصل الإعلان لكل الموظفين النشطين، ويظهر لهم <strong>بنصّه كاملًا</strong> فور فتح
              الشاشة — لا يحتاج أحدهم أن يفتح رسالةً ليعرف ما فيها.
            </p>
          </section>

          {me?.isOwner && <TipsPanel onPick={(t, b) => { setTitle(t); setBody(b); setSeverity('INFO'); setDone(null); }} />}

          <div className="mt-3 space-y-3">
            <Field label="العنوان" required>
              <TextInput value={title} maxLength={120} onChange={e => setTitle(e.target.value)} />
            </Field>
            <Field label="النوع">
              <Select value={severity} options={SEVERITIES} onChange={e => setSeverity(e.target.value)} />
            </Field>
            <Field label="نصّ الإعلان" required>
              <TextArea rows={6} value={body} maxLength={4000} onChange={e => setBody(e.target.value)} />
            </Field>
          </div>

          {error && <div className="mt-3"><Alert>{error}</Alert></div>}
          {done && <div className="mt-3"><Alert kind="success">{done}</Alert></div>}

          <button type="button" className="btn-primary mt-4 w-full px-4 py-2"
            disabled={!ready || busy} onClick={() => void send()}>
            {busy ? 'جارٍ الإرسال…' : 'إرسال الإعلان'}
          </button>
        </Drawer>
      )}
    </>
  );
}
